import { computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, tap } from 'rxjs';
import { environment } from '../environments/environment';

/**
 * A capped list of favourites kept on the server (favourite albums, favourite artists).
 * `ids` is loaded cheaply for the heart buttons on every page; `items` is the full list, loaded by the list page.
 */
export abstract class FavouriteListService<T extends { id: string }> {
  /** Route under the user API, e.g. 'favourite-albums'. */
  protected abstract readonly path: string;
  /** Plural display name used in messages, e.g. 'Favourite Albums'. */
  protected abstract readonly noun: string;
  abstract readonly max: number;

  protected http = inject(HttpClient);
  protected snackBar = inject(MatSnackBar);

  /** Full saved list once loaded, otherwise null. */
  readonly items = signal<T[] | null>(null);
  /** Ids of saved items, for the heart buttons on every page. */
  readonly ids = signal<ReadonlySet<string>>(new Set());
  readonly count = computed(() => this.ids().size);

  private idsRequested = false;

  private get base(): string {
    return `${environment.userAPIBase}/${this.path}`;
  }

  /** Loads the saved ids once per session so hearts show the right state. */
  ensureIds(): void {
    if (this.idsRequested) return;
    this.idsRequested = true;
    this.http.get<string[]>(`${this.base}/ids`).subscribe({
      next: (ids) => this.ids.set(new Set(ids)),
      error: (err) => {
        console.error(`Error loading ${this.noun} ids:`, err);
        this.idsRequested = false;
      }
    });
  }

  loadAll(): Observable<T[]> {
    return this.http.get<T[]>(this.base).pipe(
      tap(list => {
        this.items.set(list);
        this.ids.set(new Set(list.map(item => item.id)));
      })
    );
  }

  add(id: string): Observable<T> {
    return this.http.put<T>(`${this.base}/${id}`, {}).pipe(
      tap(saved => {
        this.ids.update(ids => new Set(ids).add(saved.id));
        this.items.update(list => list && !list.some(item => item.id === saved.id) ? [...list, saved] : list);
      })
    );
  }

  remove(id: string): Observable<unknown> {
    return this.http.delete(`${this.base}/${id}`).pipe(
      tap(() => {
        this.ids.update(ids => {
          const next = new Set(ids);
          next.delete(id);
          return next;
        });
        this.items.update(list => list ? list.filter(item => item.id !== id) : list);
      })
    );
  }

  /** Heart button behaviour: save or unsave, and tell the user what happened. */
  toggle(id: string, name: string): void {
    if (this.ids().has(id)) {
      this.remove(id).subscribe({
        next: () => this.snackBar.open(`Removed “${name}” from ${this.noun}`, 'OK', { duration: 2000 }),
        error: (err) => {
          console.error(`Error removing from ${this.noun}:`, err);
          this.snackBar.open('Unable to remove this. Please try again.', 'OK', { duration: 3000 });
        }
      });
    } else {
      this.add(id).subscribe({
        next: () => this.snackBar.open(`Added “${name}” to ${this.noun}`, 'OK', { duration: 2000 }),
        error: (err) => {
          console.error(`Error adding to ${this.noun}:`, err);
          const message = err?.error?.code === 'LIMIT'
            ? `You have reached the limit of ${this.max} ${this.noun.toLowerCase()}.`
            : 'Unable to add this. Please try again.';
          this.snackBar.open(message, 'OK', { duration: 4000 });
        }
      });
    }
  }
}
